import React from 'react';
import { Box, Card, Stack, Typography, useTheme } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { Counter } from '../../motion';
import { semantic } from '../../theme/tokens';
import type { SemanticTone } from '../../theme/tokens';

interface StatCardProps {
  label: string;
  value: number;
  /** Rendered around the number, e.g. "$" or "%". */
  prefix?: string;
  suffix?: string;
  icon?: React.ReactNode;
  /** Accent used for the icon tile. */
  tone?: SemanticTone;
  /** Percentage change vs. the previous period. */
  delta?: number;
  /** Secondary line under the value. */
  caption?: string;
}

/**
 * Headline metric tile for dashboards. The value counts up on first render,
 * which is the only motion these cards get — they're read all day.
 */
export const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  prefix,
  suffix,
  icon,
  tone = 'info',
  delta,
  caption,
}) => {
  const sem = semantic[useTheme().palette.mode];
  const toneColor = sem[tone];
  const deltaPositive = (delta ?? 0) >= 0;

  return (
    <Card sx={{ p: 2.5, height: '100%' }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="overline" color="text.secondary" noWrap>
            {label}
          </Typography>

          <Typography variant="h4" sx={{ mt: 0.5, fontVariantNumeric: 'tabular-nums' }}>
            <Counter value={value} prefix={prefix} suffix={suffix} />
          </Typography>

          {(caption || delta !== undefined) && (
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.75 }}>
              {delta !== undefined && (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.25}
                  sx={{
                    color: deltaPositive ? sem.success.dark : sem.danger.dark,
                    '& svg': { fontSize: 16 },
                  }}
                >
                  {deltaPositive ? <TrendingUpIcon /> : <TrendingDownIcon />}
                  <Typography variant="caption" fontWeight={650} color="inherit">
                    {Math.abs(delta)}%
                  </Typography>
                </Stack>
              )}
              {caption && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {caption}
                </Typography>
              )}
            </Stack>
          )}
        </Box>

        {icon && (
          <Box
            sx={{
              flexShrink: 0,
              width: 40,
              height: 40,
              borderRadius: 2,
              display: 'grid',
              placeItems: 'center',
              color: toneColor.dark,
              backgroundColor: toneColor.light,
              '& svg': { fontSize: 21 },
            }}
          >
            {icon}
          </Box>
        )}
      </Stack>
    </Card>
  );
};

export default StatCard;
